import { useState, type Ref } from 'react';
import {
	Pressable,
	StyleSheet,
	Text,
	View,
	type GestureResponderEvent,
	type StyleProp,
	type TextStyle,
	type ViewStyle,
} from 'react-native';
import LoadingIndicator from 'src/presentation/components/molecules/LoadingIndicator';
import Colors from 'src/core/utils/Colors';
import Fonts from 'src/core/utils/Fonts';
import Dimens from 'src/core/utils/Dimens';
import SvgIcon from 'src/presentation/components/atoms/SvgIcon';
import { type SvgIconType } from '@thermenresorts/icons';

const styles = StyleSheet.create({
	button: {
		justifyContent: 'center',
		alignItems: 'center',
		borderWidth: Dimens.borderWidth1,
		height: Dimens.buttonHeight50,
		borderRadius: Dimens.borderRadius99,
	},
	content: {
		flexDirection: 'row',
		alignItems: 'center',
		justifyContent: 'center',
	},
	label: {
		textTransform: 'uppercase',
		textAlign: 'center',
		fontFamily: Fonts.barlowMedium,
		fontSize: Dimens.fontSize14,
		lineHeight: Dimens.lineHeight16,
	},
	horizontalPadding: {
		paddingHorizontal: Dimens.padding16,
	},
	contentLoading: {
		opacity: Dimens.opacity0,
	},
	loadingOverlay: {
		position: 'absolute',
		left: Dimens.padding0,
		right: Dimens.padding0,
		top: Dimens.padding0,
		bottom: Dimens.padding0,
		alignItems: 'center',
		justifyContent: 'center',
	},
});

interface ButtonProps {
	label?: string | undefined;
	ref?: Ref<View>;
	backgroundColor?: string;
	textColor?: string;
	borderColor?: string;
	onPress?: ((event: GestureResponderEvent) => void) | undefined;
	extraStyle?: ViewStyle;
	buttonStyle?: StyleProp<ViewStyle>;
	textStyle?: StyleProp<TextStyle>;
	loading?: boolean;
	disabled?: boolean;
	svgIcon?: SvgIconType;
	iconColor?: string;
	iconSize?: number;
}

export function Button({
	label,
	ref,
	backgroundColor,
	textColor,
	borderColor,
	onPress,
	extraStyle,
	buttonStyle,
	textStyle,
	loading,
	disabled,
	svgIcon,
	iconColor,
	iconSize,
}: ButtonProps) {
	const [isPressed, setIsPressed] = useState(false);
	const handlePress = (isPressed: boolean) => {
		setIsPressed(isPressed);
	};
	const bgColor = backgroundColor ?? Colors.green;
	const bColor = borderColor ?? backgroundColor;
	const isBlocked = loading === true || disabled === true;
	return (
		<Pressable
			disabled={isBlocked}
			ref={ref}
			style={extraStyle}
			onPress={isBlocked ? null : onPress}
			onPressIn={() => handlePress(true)}
			onPressOut={() => handlePress(false)}
		>
			<View
				accessibilityState={{ disabled: isBlocked }}
				accessible={true}
				style={[
					styles.button,
					{
						backgroundColor: bgColor,
						borderColor: bColor ?? bgColor,
						opacity: disabled ? Dimens.opacity50 : isPressed ? Dimens.opacity80 : Dimens.opacity100,
					},
					buttonStyle,
				]}
			>
				<View style={[styles.content, loading ? styles.contentLoading : null]}>
					{svgIcon && <SvgIcon name={svgIcon} size={iconSize} tintColor={iconColor} />}
					<Text
						style={[
							styles.label,
							{
								color: textColor ?? Colors.white,
								marginLeft: svgIcon ? Dimens.padding8 : Dimens.padding0,
							},
							textStyle,
						]}
					>
						{label}
					</Text>
				</View>
				{loading ? (
					<View style={styles.loadingOverlay}>
						<LoadingIndicator color={textColor ?? Colors.white} size={'small'} />
					</View>
				) : null}
			</View>
		</Pressable>
	);
}

export function PrimaryButton(props: ButtonProps) {
	return (
		<Button
			backgroundColor={Colors.green}
			buttonStyle={styles.horizontalPadding}
			iconColor={Colors.white}
			textColor={Colors.white}
			{...props}
		/>
	);
}

export function SecondaryButton(props: ButtonProps) {
	return (
		<Button
			backgroundColor={Colors.gray75}
			borderColor={Colors.gray200}
			buttonStyle={styles.horizontalPadding}
			iconColor={Colors.gray800}
			textColor={Colors.gray800}
			{...props}
		/>
	);
}

export function TertiaryButton(props: ButtonProps) {
	return (
		<Button
			backgroundColor={Colors.gold}
			borderColor={Colors.gold}
			buttonStyle={styles.horizontalPadding}
			iconColor={Colors.white}
			textColor={Colors.white}
			{...props}
		/>
	);
}

export function TertiaryOutlineButton(props: ButtonProps) {
	return (
		<Button
			backgroundColor={Colors.transparent}
			borderColor={Colors.gold}
			buttonStyle={styles.horizontalPadding}
			iconColor={Colors.gray900}
			textColor={Colors.gray900}
			{...props}
		/>
	);
}
